import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, CATEGORIES } from "@/lib/api";
import { AI_MODELS, streamSSE } from "@/lib/stream";

const EMPTY = { name: "", description: "", category: "apparel", price: "", stock: "", image: "", sizes: "", colors: "", variants: "", specs: "", featured: false, is_digital: false };
const input = "w-full h-10 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-3 text-sm";

const F = ({ label, children }) => (
  <label className="block"><span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">{label}</span><div className="mt-1">{children}</div></label>
);

export const ProductFormDialog = ({ open, onOpenChange, product, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [writing, setWriting] = useState(false);
  const [aiModel, setAiModel] = useState("claude");
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((prev) => ({ ...prev, image: `${process.env.REACT_APP_BACKEND_URL}${data.url_path}` }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const writeDescription = async () => {
    if (!f.name) return toast.error("Add a product name first");
    setWriting(true);
    setF((prev) => ({ ...prev, description: "" }));
    try {
      await streamSSE("/admin/ai/description", { name: f.name, category: f.category, specs: f.specs, model: aiModel },
        (d) => setF((prev) => ({ ...prev, description: prev.description + d })));
    } catch (err) { toast.error(err.message); } finally { setWriting(false); }
  };

  useEffect(() => {
    setF(product ? { ...product, sizes: product.sizes.join(", "), colors: product.colors.join(", "),
      variants: (product.variants || []).map((v) => [v.name, v.price, v.servings ?? ""].join(" | ")).join("\n") } : EMPTY);
  }, [product, open]);

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const parseVariants = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [name, price, servings] = l.split("|").map((x) => x.trim());
    return { name, price: parseFloat(price), servings: servings ? parseInt(servings, 10) : null };
  });

  const save = async (e) => {
    e.preventDefault();
    const variants = parseVariants(f.variants);
    if (variants.some((v) => !v.name || !(v.price > 0))) return toast.error("Each variant line needs: name | price | servings (optional)");
    setSaving(true);
    const body = { ...f, price: parseFloat(f.price), stock: parseInt(f.stock, 10), sizes: list(f.sizes), colors: list(f.colors), variants };
    delete body.id; delete body.created_at; delete body.rating_avg; delete body.rating_count;
    try {
      product ? await api.put(`/admin/products/${product.id}`, body) : await api.post("/admin/products", body);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="bg-[#121215] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display uppercase font-black text-2xl">{product ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Upload a photo of your ABS gear and let AI write the copy.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><F label="Name"><input required data-testid="product-form-name" className={input} value={f.name} onChange={on("name")} /></F></div>
          <F label="Category">
            <select data-testid="product-form-category" className={input} value={f.category} onChange={on("category")}>
              {CATEGORIES.filter((c) => c.id !== "all").map((c) => <option key={c.id} value={c.id} className="bg-black">{c.label}</option>)}
            </select>
          </F>
          <F label="Price (USD)"><input required type="number" step="0.01" min="0.5" data-testid="product-form-price" className={input} value={f.price} onChange={on("price")} /></F>
          <F label="Stock"><input required type="number" min="0" data-testid="product-form-stock" className={input} value={f.stock} onChange={on("stock")} /></F>
          <F label="Featured"><div className="h-10 flex items-center"><Switch data-testid="product-form-featured" checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} /></div></F>
          <div className="col-span-2">
            <F label="Product photo">
              <div className="flex gap-3 items-center">
                {f.image && <img src={f.image} alt="" data-testid="product-form-image-preview" className="w-14 h-16 object-cover border border-white/10" />}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={upload} className="hidden" data-testid="product-form-file-input" />
                <button type="button" data-testid="product-form-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-10 px-4 border border-white/20 hover:bg-white hover:text-black text-sm font-display uppercase font-bold tracking-wider flex items-center gap-2 disabled:opacity-50">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                </button>
                <input required data-testid="product-form-image" className={input} value={f.image} onChange={on("image")} placeholder="or paste image URL" />
              </div>
            </F>
          </div>
          <F label="Sizes (comma separated)"><input data-testid="product-form-sizes" className={input} value={f.sizes} onChange={on("sizes")} placeholder={f.is_digital ? "Digital download" : "S, M, L"} /></F>
          <F label="Colors (comma separated)"><input data-testid="product-form-colors" className={input} value={f.colors} onChange={on("colors")} placeholder="Black, Red" /></F>
          <div className="col-span-2"><F label="Variant pricing (one per line: size | price | servings)"><textarea data-testid="product-form-variants" className={`${input} h-16 py-2 font-mono`} value={f.variants} onChange={on("variants")} placeholder={"2 lb | 54 | 30\n5 lb | 109 | 75"} /></F></div>
          <div className="col-span-2 flex items-center justify-between border border-white/10 px-3 h-11">
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Digital product (no shipping, instant access)</span>
            <Switch data-testid="product-form-digital" checked={f.is_digital} onCheckedChange={(v) => setF({ ...f, is_digital: v })} />
          </div>
          <div className="col-span-2"><F label="Specs"><input data-testid="product-form-specs" className={input} value={f.specs} onChange={on("specs")} placeholder="Material, fit, capacity..." /></F></div>
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Description</span>
              <div className="flex items-center gap-2">
                <select data-testid="ai-model-select" value={aiModel} onChange={(e) => setAiModel(e.target.value)} className="bg-white/5 border border-white/15 h-7 px-1 text-[10px] font-mono uppercase">
                  {AI_MODELS.map((m) => <option key={m.id} value={m.id} className="bg-black">{m.label}</option>)}
                </select>
                <button type="button" data-testid="ai-write-description-button" onClick={writeDescription} disabled={writing}
                  className="h-7 px-2 bg-[#FF3B30]/15 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white text-[11px] font-mono uppercase flex items-center gap-1 disabled:opacity-50">
                  {writing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI write
                </button>
              </div>
            </div>
            <textarea data-testid="product-form-description" className={`${input} h-24 py-2`} value={f.description} onChange={on("description")} />
          </div>
          <button data-testid="product-form-submit" disabled={saving} className="col-span-2 h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider disabled:opacity-60">
            {saving ? "Saving..." : "Save product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
